// Story 7.12 Iteration 2 (ADO-911), AC6/AC7/AC9 + DR-5/DR-6 — the Linked Product
// upsell that opens on a successful add to basket. This is the single HOST for
// BOTH presentations (design refresh, 2026-08-14): the full focus-trapped MODAL
// (below) for a PDP add, and the non-invasive corner `UpsellToast` for a search
// or listing add. The add's origin decides which, carried in the store's
// `openVariant`; this host renders whichever is asked for and owns every shared,
// timing-sensitive concern once, so the two presentations cannot drift.
//
// PO acceptance criteria, verbatim:
//   AC6 — "The Linked Products will be a set of SKUs sent from ERP."
//   AC7 — "The Linked Product Modal will appear on click of Add to basket."
//   AC9 — "If there are no Linked Products the modal will not appear."
//
// CONTENT = LINKED PRODUCTS (FR106), not the FR107 co-purchase signal. The design
// heading reads "Customers also bought these", which is the co-purchase wording,
// but AC6/AC7 make the CONTENT Linked Products — so the design copy is surfaced
// while the data comes from the `related` (LINKED) leg of `IRecommendationPort`.
// That heading-vs-data mismatch is raised with the PO as OQ-15 and is deliberate,
// not an oversight. (Iteration 1 initially assumed FR107 here; AC6/AC7 corrected it.)
//
// TRIGGERS — every add-to-basket entry point (Marika, 2026-07-29): the PDP hero
// Add, quick-add, add-from-listing (Story 7.9 `ProductCard`), the substitutes row
// and the carousel tiles. They all funnel through `useAddConfirmation`, which
// calls the ONE `useUpsellModalStore.openUpsell(sku, name, variant)` seam. This
// host is mounted once at the composition root (`main.tsx`) so there is exactly
// one upsell for all of them.
//
// AC9 is enforced in the store, not here: `openUpsell` resolves the linked
// products first and never opens when there are none. This host therefore only
// ever renders (modal or toast) with at least one card.
//
// a11y (NFR24–26) — MODAL variant: built on the design-system `Modal` primitive,
// so it inherits the focus trap, initial focus, focus-restore-on-close,
// Escape-to-dismiss, backdrop dismiss, body-scroll lock and the portal to <body>.
// Design deviation: the Figma puts the "{product} added to order" badge ABOVE the
// title; `Modal` renders title-then-description, so the badge rides the
// description slot. That trade keeps the shared, already-tested dialog contract
// rather than forking it. (The TOAST variant is deliberately NOT a dialog — see
// `UpsellToast`.)

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { Button, Modal } from '../../design-system';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useMultibuyDeals } from '../../multibuy/useMultibuyDealsStore';
import { useSessionDepotId } from '../../availability/session-depot';
import { useBasketStore } from '../../basket/useBasketStore';
import { totalCases, UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { settledCartValue } from '../../basket/cartMoney';
import { useCartLinesLoaded } from '../../basket/useCartValue';
import { formatMoney } from '../../shared/money';
import { useRecommendationsEntry } from '../../recommendations/useRecommendationsEntry';
// The ONE recommendation-row → card-row adapter, shared with the PDP carousel.
// It used to be a verbatim copy in each of the two surfaces, and the stub shape
// it produces is what `useProductDetailStore`'s `partial` repair is documented
// against — so the two copies had to move together (PR #168 review).
import { toProductSummary } from '../../recommendations/toProductSummary';
import {
  MAX_UPSELL_CARDS,
  useUpsellModalStore,
  type UpsellVariant,
} from '../../upsell/useUpsellModalStore';
import { ProductCard } from '../Browse/ProductCard';
import { UpsellToast } from './UpsellToast';
import styles from './UpsellModal.module.css';

export function UpsellModal() {
  const openSku = useUpsellModalStore((s) => s.openSku);
  const openProductName = useUpsellModalStore((s) => s.openProductName);
  const openVariant = useUpsellModalStore((s) => s.openVariant);
  const close = useUpsellModalStore((s) => s.close);
  const dismissPending = useUpsellModalStore((s) => s.dismissPending);

  // AC6 — LINKED products (the `related` leg), capped at three (DR-5). DEPOT-SCOPED
  // (7-FU-I; @ADO-1222): this modal is mounted at the composition root and stays open across
  // a header depot switch, so a raw `bySku[openSku]` read would keep offering the previous
  // depot's linked products — with working Add buttons — until a refetch nobody triggers.
  // A mismatched entry reads as not-loaded, so the card list empties instead.
  const related = useRecommendationsEntry(openSku)?.related ?? null;
  const cards = (related ?? []).slice(0, MAX_UPSELL_CARDS);

  // The basket summary is DERIVED below the early return, not here: this
  // component is mounted at the composition root and closed almost all of the
  // time, and it subscribes to `s.lines`, so every basket mutation anywhere in the
  // app re-renders it. Reducing the whole basket here meant doing that work on
  // renders that draw nothing. (The hook itself has to stay above the return.)
  const lines = useBasketStore((s) => s.lines);
  // Whether those lines are the loaded basket, so the cart value
  // below can be summed only when the summary is drawn.
  const linesLoaded = useCartLinesLoaded();
  const pending = useBasketStore((s) => s.pending);

  // Populate availability for the card SKUs so each Add has its UNAVAILABLE gate
  // (7.11 D-10 client-side posture). Called unconditionally — the hook no-ops on
  // an empty list, and hooks must not sit behind the early return below.
  useAvailability(cards.map((c) => c.sku));
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(cards.map((c) => c.sku));
  // Pricing migration 32 (ADO-3985): the same products' multi-buy deals, one request (D-5: no-op
  // unless VITE_REAL_PRICES_ENABLED is on).
  useMultibuyDeals(cards.map((c) => c.sku));

  // Dismiss on navigation, in BOTH orderings. The modal is mounted at the
  // composition root, OUTSIDE <Routes>, so a route change does not unmount it —
  // and its cards are real `ProductCard`s whose titles are router <Link>s to
  // `/products/:sku`.
  //
  //   • navigate AFTER open — clicking a linked product (or the browser Back
  //     button) navigated underneath the dialog, stranding the user on the new
  //     page behind a stale `aria-modal` overlay with the focus trap and
  //     body-scroll lock still engaged. The location key at open time is
  //     recorded on the first pass; any later key means a real navigation
  //     happened while open, so close.
  //   • navigate BEFORE open (PR #168 review) — `openUpsell` awaits the
  //     recommendations fetch before it commits, so add-from-listing → click a
  //     card title opened the modal OVER the page just landed on, for a product
  //     on the previous screen. On a cold SKU (every add outside the PDP, since
  //     only `PdpRecommendations` pre-warms) that window is a full round trip.
  //     Nothing bumped the store's generation on a navigation, so a navigation
  //     while CLOSED calls `dismissPending()` to make that pending open stale.
  const location = useLocation();
  const openedAtKey = useRef<string | null>(null);
  const lastKey = useRef(location.key);
  // The open state can go OPEN-TO-OPEN in a single `set`: a persisted toast is
  // still up (it survives navigation by design) when a PDP add opens the modal,
  // so `openUpsell` replaces the sku/variant without ever passing through `null`.
  // Tracking the previous sku is what tells a genuinely fresh open from a re-run
  // of this effect — without it `openedAtKey` kept the key stamped when the TOAST
  // opened, and the modal that replaced it read as "opened, then navigated" and
  // closed on the same pass. The PDP upsell was dead whenever a persisted toast
  // was on screen after any navigation.
  const prevOpenSku = useRef<string | null>(null);
  const prevOpenVariant = useRef<UpsellVariant | null>(null);
  useEffect(() => {
    // Compare against the PREVIOUS key rather than assuming every run is a
    // navigation: this effect also runs on `openSku` changes, and the first run
    // is a mount, neither of which may cancel a legitimately pending open.
    const navigated = lastKey.current !== location.key;
    lastKey.current = location.key;

    // A fresh open — closed-to-open, or open-to-open replacing the sku OR the
    // variant in one set. Comparing the (sku, VARIANT) pair is what catches a
    // same-sku toast → modal transition (product X's toast up → navigate to X's
    // own PDP → hero Add for X opens `openUpsell(X, 'modal')`, changing only the
    // variant): an sku-only check would keep the stale key and close the modal on
    // the same pass — the original bug through a gap in the freshness test.
    const freshlyOpened =
      openSku !== null &&
      (openSku !== prevOpenSku.current || openVariant !== prevOpenVariant.current);
    prevOpenSku.current = openSku;
    prevOpenVariant.current = openVariant;

    // ANY navigation makes a still-pending `openUpsell` stale so it cannot commit
    // onto the page just landed on (PR #168). It is generation-only (no render),
    // so it is safe to run while a toast is OPEN too — and it MUST: the
    // toast-exempt `close()` below never bumps the generation, so a navigation
    // with a toast up used to leave a pending open to surface on the new page.
    if (navigated) dismissPending();

    if (openSku === null) {
      openedAtKey.current = null;
      return;
    }
    // Stamp the key this open began at — on a fresh open, or on the first pass for
    // an open that was already committed when this host mounted.
    if (freshlyOpened || openedAtKey.current === null) {
      openedAtKey.current = location.key;
      return;
    }
    // A real navigation while open. The MODAL must dismiss — a focus-trapped,
    // scroll-locked dialog stranded over the page the user just navigated to is
    // broken. The TOAST must NOT: it is a non-invasive corner popup, and the PO
    // (Peter Eastwood, ADO-911 review 2026-08-19) asked that it disappear ONLY on
    // its own X / Dismiss / Escape, so a route change deliberately leaves it up.
    if (openedAtKey.current !== location.key && openVariant !== 'toast') close();
  }, [openSku, openVariant, location.key, close, dismissPending]);

  // CLOSE on a depot switch — not merely stop rendering. The depot-scoped read above makes a
  // mismatched entry read as not-loaded, which empties `cards` and sends the early return
  // below down the `cards.length === 0` path. That HIDES the dialog; it does not close it.
  // `openSku` stays set and `bySku` is SKU-keyed, so switching BACK made the entry match again
  // and the modal reappeared unprompted, long after the add that triggered it — and this host
  // has no depot-keyed load effect to refetch in between. Clearing `openSku` is what actually
  // ends it, and it also restores focus through `Modal`'s own close path rather than leaving a
  // dialog that vanished from under the user.
  const sessionDepotId = useSessionDepotId();
  // `undefined` = nothing recorded yet, which is NOT the same as `null` = recorded, and the
  // depot was inactive. Both meanings on `null` made the sentinel swallow a real switch: a
  // modal opened with no active depot recorded `null`, so the FIRST switch away from it read
  // as the not-yet-recorded first pass and merely initialised the ref instead of closing. The
  // dialog then survived the switch — hidden, because the depot-scoped read empties `cards`,
  // but with `openSku` still set, so it came back the moment the depot did (round 15, F5).
  const openedAtDepot = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (openSku === null) {
      openedAtDepot.current = undefined;
      return;
    }
    // Record the depot on the first pass, the same shape as the location-key guard above, so
    // the mount itself is never mistaken for a switch.
    if (openedAtDepot.current === undefined) {
      openedAtDepot.current = sessionDepotId;
      return;
    }
    if (openedAtDepot.current !== sessionDepotId) close();
  }, [openSku, sessionDepotId, close]);

  // AC9 backstop: the store never opens without linked products, but if an entry
  // were cleared underneath us, render nothing rather than an empty dialog.
  if (openSku === null || cards.length === 0) return null;

  // DR-5: "Your basket · €X · N items". The € is the cart value:
  // the lines summed at the prices the server locked on them (`useCartValue`), the same
  // figure the header cart button shows (`useCartValue`'s rule, summed here only when the
  // summary is drawn), or UNKNOWN_TOTAL while it is not known (a line
  // the server has not priced yet, a basket that has not loaded). A product-surface price
  // or a made-up figure never stands in for it. The COUNT is the basket's cases. Shared by
  // both presentations. Pricing migration 31 (ADO-1958): the cases include the free units.
  const itemCount = totalCases(lines);
  const value = linesLoaded ? settledCartValue(lines, pending) : null;
  const basketValue = value === null ? UNKNOWN_TOTAL : formatMoney(value);

  // Design refresh — a search/listing add renders the non-invasive TOAST instead
  // of the modal. Same cards, same count, same close; a different presentation.
  if (openVariant === 'toast') {
    return (
      <UpsellToast
        productName={openProductName ?? 'Product'}
        cards={cards}
        itemCount={itemCount}
        cartValue={value}
        onClose={close}
      />
    );
  }

  return (
    <Modal
      open
      onClose={close}
      size="wide"
      title="Customers also bought these"
      description={
        <span className={styles.description}>
          <span className={styles.addedBadge}>
            <span className={styles.addedDot} aria-hidden="true" />
            {openProductName ?? 'Product'} added to order
          </span>
          <span>Add them now and save time on your next order.</span>
        </span>
      }
      footer={
        <div className={styles.footer}>
          <p className={styles.basketSummary}>
            <span className={styles.basketLabel}>Your basket</span>
            <span className={styles.basketValue}>
              <span data-testid="upsell-basket-value">{basketValue}</span>
              {' · '}
              <span>
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            </span>
          </p>
          <div className={styles.footerActions}>
            <button type="button" className={styles.continueButton} onClick={close}>
              No thanks, continue shopping
            </button>
            {/* The basket SCREEN is Epic 8 — there is no /basket route yet.
                DISABLED, with the coming-soon signal VISIBLE (PR #168 review).
                It previously rendered as an enabled primary "Go to basket" that
                just dismissed, with "(coming soon)" only in the `aria-label` —
                so a sighted user read the modal's primary action as working and
                got a dismissal. The hero zoom, "View Offer" and "WOW Price" are
                all disabled in this story on the argument that an enabled
                control which does nothing reads as BROKEN rather than unbuilt;
                the primary action has the least claim to an exemption. The
                arrow is dropped with the action — it promised a destination.
                Dismissal stays on "No thanks", the X, Escape and the scrim. */}
            <Button
              variant="primary"
              disabled
              title="The basket screen is not available yet"
              aria-label="Go to basket (coming soon)"
              className={styles.goToBasket}
            >
              Go to basket <span className={styles.comingSoon}>coming soon</span>
            </Button>
          </div>
        </div>
      }
    >
      {/* The design draws these as the shared grid ProductCard (Figma 1269:7109),
          so the modal mounts the real card — same decision as the PDP carousel.
          `suppressUpsell` stops an add from inside the modal stacking a second one. */}
      <ul className={styles.cards}>
        {cards.map((item) => (
          <li key={item.sku} className={styles.card}>
            <ProductCard
              product={toProductSummary(item)}
              suppressUpsell
              hideRrpAndMargin
              partialRow
            />
          </li>
        ))}
      </ul>
    </Modal>
  );
}
